import React, { useCallback, useEffect, useRef, useState } from "react";

import Logo from "images/gvi-logo2.inline.svg";
import ArrowRight from "images/icons/arrow-right.inline.svg";
import BackFlower from "images/illustrations/back-flower.inline.svg";
import MountainIllustration from "images/illustrations/Mountain_illustration.inline.svg";

import { useTheme } from "../../context/ThemeContext";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import ContentCard from "../cards/contentCard";
import { ConditionalSwiper } from "../shared/conditionalSwiper";

const VolunteerProgramFocus = ({
  hasCommunityPrograms,
  focusBlocks,
  containerClass = "pb-6 sm:py-20 bg-generic-bg planet:gvi-light-gray-bg",
  innerContainerClass = "container z-10 flex flex-col w-11/12 lg:w-9/12 pt-12 sm:pt-10",
  headingClass = "w-full h-16 py-2 bg-inherit md:h-24 md:w-8/12 md:bg-yellow-light planet:md:gvi-dark-gray-bg",
  headingContainerClass = "static flex flex-col w-full -top-12 md:absolute",
  titleClass = "text-left sm:text-center",
  cardBoxCoverClass = "sm:gvi-program-card-box bg-transparent",
  descriptionContainerClass = "pt-3 pb-4",
  contentWrapperClass = "flex flex-col p-6 rounded-b bg-white planet:gvi-dark-gray-bg",
  defaultVisibleCommunityCards = 6
}) => {
  const { colorMode, currentBrand, peopleDomain, planetDomain } = useTheme();

  const [communitySite, setCommUrl] = useState("");
  const [conservationSite, setConsUrl] = useState("");

  const [visibleCnt, setVisibleCnt] = useState(defaultVisibleCommunityCards);
  const [, setPagination] = useState(true);
  const breakpoint = useBreakpoint();

  useEffect(() => {
    currentBrand.includes("people")
      ? setConsUrl(planetDomain)
      : setCommUrl(peopleDomain);
  }, [currentBrand, peopleDomain, planetDomain]);

  const communityHeading = focusBlocks.vafCommunityTitle ?? "";
  const communityCards =
    focusBlocks && focusBlocks?.vafComBlocks
      ? focusBlocks?.vafComBlocks.map((comCard, cidx) => ({
          id: cidx,
          img: comCard.vafComImage?.sourceUrl ?? "",
          title: comCard.vafComTitle,
          description: comCard.vafComDesc,
          url: comCard.vafComUrl
        }))
      : [];

  const conservationHeading = focusBlocks?.vafConservationTitle ?? "";
  const conservationCards =
    focusBlocks && focusBlocks?.vafConBlocks
      ? focusBlocks?.vafConBlocks.map((conCard, cidx) => ({
          id: cidx,
          img: conCard.vafConImage?.sourceUrl ?? "",
          title: conCard.vafConTitle,
          description: conCard.vafConDesc,
          url: conCard.vafConUrl
        }))
      : [];

  useEffect(() => {
    if (visibleCnt > communityCards?.length) setPagination(false);
  }, [communityCards.length, visibleCnt]);

  const handlePagination = useCallback(() => {
    setVisibleCnt((prevState) => prevState + 6);
  }, []);

  return (
    <>
      <section
        className={`relative flex flex-col items-center justify-center w-full mx-auto ${containerClass}`}
        id="view-programs"
      >
        {colorMode === "light" ? (
          <MountainIllustration className="absolute w-[69%] -right-[18vw] -bottom-[16vw] hidden md:block xl:w-2/5" />
        ) : (
          <BackFlower className="absolute z-0 hidden -bottom-40 -left-16 md:block stroke-white" />
        )}
        <div className={headingContainerClass}>
          <div
            className={`flex flex-col justify-center mx-auto ${headingClass} px-4`}
          >
            <h1
              className={`font-light newsletter-heading ${titleClass}`}
              dangerouslySetInnerHTML={{
                __html: "Choose a program focus"
              }}
            />
          </div>
        </div>
        <div className={innerContainerClass}>
          <div className="flex flex-col-reverse sm:flex-row-reverse flex-wrap w-full planet:flex-row sm:flex-nowrap justify-between">
            <div
              className={`w-full mt-10 planet:mt-0  planet:sm:mt-10 sm:mt-10 ${
                hasCommunityPrograms ? "md:w-6/12 h-auto" : " "
              }`}
            >
              <div className="flex items-center md:pl-14 planet:pl-0">
                <Logo className="w-8 fill-black-lighter planet:fill-white" />
                <span className="text-white pl-4 uppercase h7 !font-semibold menu-link">
                  Planet
                </span>
              </div>

              <h2
                className="pt-0 pb-4 heading md:pl-14 planet:pl-0 md:pt-2"
                dangerouslySetInnerHTML={{ __html: conservationHeading }}
              />

              <div
                className={`flex mt-4 h-auto md:pl-14 sm:planet:pl-0 sm:planet:md:pr-10 ${
                  hasCommunityPrograms ? " flex-col gap-y-5" : " flex-wrap"
                }`}
              >
                <ConditionalSwiper
                  condition={breakpoint === "xs"}
                  slidesPerView={1.25}
                  spaceBetween={15}
                  centeredSlides={false}
                  pagination={false}
                  speed={1000}
                  className="w-full h-full learn-more-swiper"
                >
                  {conservationCards &&
                    conservationCards.length > 0 &&
                    conservationCards.map((cardInfo, index) => (
                      <div
                        key={index}
                        className={`${
                          hasCommunityPrograms
                            ? "w-full"
                            : "w-full sm:w-1/2 sm:inline-block"
                        } rounded-md relative`}
                      >
                        <ContentCard
                          title={cardInfo.title}
                          description={cardInfo.description}
                          imgUrl={cardInfo.img}
                          imgHeight={"h-28 sm:h-56"}
                          url={`${conservationSite}${cardInfo.url}`}
                          type={hasCommunityPrograms ? 1 : 2}
                          cardBoxCoverClass={cardBoxCoverClass}
                          contentWrapperClass={contentWrapperClass}
                          descriptionContainerClass={descriptionContainerClass}
                          ellipse={breakpoint === "xs"}
                        />
                      </div>
                    ))}
                </ConditionalSwiper>
              </div>
            </div>

            {hasCommunityPrograms && (
              <>
                <div className="w-full planet:mt-10 sm:mt-10 md:w-6/12 ">
                  <div className="flex items-center planet:md:pl-14">
                    <Logo className="w-8 fill-black-lighter planet:fill-white" />
                    <span className="text-white pl-4 uppercase h7 !font-semibold menu-link">
                      People
                    </span>
                  </div>

                  <h2
                    className="pt-0 pb-4 pl-0 heading planet:md:pl-14 md:pt-2"
                    dangerouslySetInnerHTML={{ __html: communityHeading }}
                  />

                  <div className="mt-4 md:h-auto planet:md:pl-10 md:pr-10 planet:md:pr-0 sm:gap-5 grid sm:grid-cols-2 planet:md:border-l md:border-r planet:md:border-r-0 border-gray-lighter">
                    <ConditionalSwiper
                      condition={breakpoint === "xs"}
                      slidesPerView={1.25}
                      spaceBetween={15}
                      centeredSlides={false}
                      pagination={false}
                      speed={1000}
                      className="w-full h-full"
                    >
                      {communityCards &&
                        communityCards?.length > 0 &&
                        communityCards
                          ?.slice(0, visibleCnt)
                          ?.map((cardInfo, index) => (
                            <div
                              key={index}
                              className="w-full h-full rounded-md relative"
                            >
                              <ContentCard
                                key={index}
                                title={cardInfo.title}
                                description={cardInfo.description}
                                imgUrl={cardInfo.img}
                                imgHeight={"h-28"}
                                url={`${communitySite}${cardInfo.url}`}
                                type={2}
                                ellipse={true}
                                titleClass={"whitespace-nowrap inline-block"}
                                cardBoxCoverClass={cardBoxCoverClass}
                                contentWrapperClass={contentWrapperClass}
                                descriptionContainerClass={
                                  descriptionContainerClass
                                }
                              />
                            </div>
                          ))}
                    </ConditionalSwiper>
                  </div>

                  {breakpoint !== "xs" &&
                    communityCards?.length > visibleCnt && (
                      <div
                        className={
                          "col-span-full w-full md:px-0 px-10 pt-10 flex items-center"
                        }
                      >
                        <button
                          onClick={() => handlePagination()}
                          className="group flex flex-row items-center justify-center px-2 py-2 w-48
                bg-inherit hover:bg-black-lighter planet:hover:bg-white
                border border-black-lighter hover:border-white planet:border-white planet:hover:border-white rounded-full planet:rounded-sm
                text-sm text-black-lighter hover:text-white planet:text-white planet:hover:text-gray-darker"
                        >
                          <p className="text-base font-bold pr-4">View more</p>
                          <ArrowRight className="w-3 h-3 stroke-black-lighter group-hover:stroke-white planet:stroke-white planet:group-hover:stroke-gray-darker rotate-90" />
                        </button>
                      </div>
                    )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default VolunteerProgramFocus;
